import type {
  CamelCasedPropertiesDeep,
  OverrideProperties,
  TupleToUnion,
} from 'type-fest';

import type {
  ArtifactLifecyclePolicy as ApiArtifactLifecyclePolicy,
  ArtifactLifecyclePolicyConditions,
  IntegrationSummary as RawIntegrationSummary,
  FeedMetadata as RawFeedMetadata,
  FeedGroupMetadata as RawFeedGroupMetadata,
  Service as RawService,
  RbacManagerRoleSummary as RawRbacManagerRoleSummary,
  RbacManagerRole as RawRbacManagerRole,
  RbacManagerRoleMember as RawRbacManagerRoleMember,
  RbacManagerPermission as RawRbacManagerPermission,
  RbacManagerRoleMembership as RawRbacManagerRoleMembership,
} from '@/api/api';
import type {
  SystemStatistics as RawSystemStatistics,
  SystemStatisticsList as RawSystemStatisticsList,
  Feature,
} from '@/api/exp';
import type {
  ServiceName,
  ServiceStatusMessage,
} from '@/models/services.const';
import type { RouteParams } from '@routes/types';
import type { Role, Account } from '@features/accounts/accounts.models';

import { ROLE_PERMISSIONS } from './autogen/role-permissions';
import type {
  EntityActionParams,
  EntityServiceResponse,
} from './common.models';
import type { SYSTEM_COMPONENTS, SYSTEM_STATUS_ITEMS } from './system.const';

export {
  IntegrationType,
  IntegrationHealthState,
  IntegrationLifecycleState,
  type RegistryConfiguration,
  type RegistryConfigurationRequest as RawRegistryConfigurationRequest,
} from '@/api/api';

export type FeatureName = Required<Feature>['name'];
export type FeatureList = Record<FeatureName, boolean>;
export type { Feature };
export type GetFeaturesResponse = Partial<FeatureList>;

export type {
  ArtifactLifecyclePolicyConditions,
  RawService,
  RawFeedMetadata,
  RawFeedGroupMetadata,
  RawIntegrationSummary,
  RawRbacManagerRoleSummary,
  RawRbacManagerRole,
  RawRbacManagerRoleMember,
  RawRbacManagerPermission,
  RawRbacManagerRoleMembership,
};

export type ArtifactLifecyclePolicy = ApiArtifactLifecyclePolicy & {
  versions?: ArtifactLifecyclePolicyConditions[];
};

export type SystemModalPropTypes = {
  editAlp: {
    uuid?: string;
    version?: number;
    readonly?: boolean;
    versionTimestamp?: string;
  };
  deleteAlp: { uuid: string; name: string };
  enableAlp: { alp: ArtifactLifecyclePolicy };
  integrationDetails: {
    id: string;
  };
  viewRole: {
    role: Role;
  };
  deleteAccount: {
    account: Account;
  };
};

// --- ALP ---------------------------------------------------------------------

export type ListAlpActionParams = EntityActionParams | void;
export type GetAlpListResponse = EntityServiceResponse<
  ArtifactLifecyclePolicy[]
>;

export type GetAlpResponse = EntityServiceResponse<ArtifactLifecyclePolicy>;
export type GetAlpActionParams = EntityActionParams & {
  uuid: string;
};

export type PutAlpResponse = EntityServiceResponse<ArtifactLifecyclePolicy>;
export type UpdateAlpActionParams = EntityActionParams &
  ArtifactLifecyclePolicy;

export type CreateAlpActionParams = EntityActionParams &
  ArtifactLifecyclePolicy;
export type PostAlpResponse = EntityServiceResponse<ArtifactLifecyclePolicy>;

export type DeleteAlpActionParams = EntityActionParams & {
  uuid: string;
};
export type DeleteAlpResponse = EntityServiceResponse<Record<string, never>>;

// --- User Roles  -------------------------------------------------------------

export type RoleName = keyof typeof ROLE_PERMISSIONS;
export type RoleNames = RoleName[];

// Derived from the autogenerated role-permissions mapping so the role list
// stays in sync with what the RBAC service exposes — see scripts/update-permissions.ts.
export const ALL_ROLES = Object.keys(ROLE_PERMISSIONS) as RoleName[];

export type UserAcctRolesType = {
  name: string;
  roles: RoleNames;
};

export interface UpdateUserRolesParams extends EntityActionParams {
  oldRoles: RoleNames;
  newRoles: RoleNames;
  oldAccts: UserAcctRolesType[];
  newAccts: UserAcctRolesType[];
  oldUserGroups: string[];
  newUserGroups: string[];
  wasAdminUser: boolean;
  isAdminUser: boolean;
}

// --- Usage  ------------------------------------------------------------------

export type SystemStatisticsType =
  | RawSystemStatistics['name']
  | 'stateless_sbom_evaluation'
  | 'artifact_sbom_creation'
  | 'image_reanalysis'; // TODO: Awaiting Platform update
export type SystemStatisticsMetricBasis = RawSystemStatistics['metric_basis'];

export const SYSTEM_STATISTICS_TYPES: SystemStatisticsType[] = [
  'source_analysis',
  'image_analysis',
  'image_reanalysis',
  'artifact_sbom_creation',
  'report_generation',
  'policy_evaluation',
  'policy_creation',
  'stateless_sbom_analysis',
  'stateless_sbom_evaluation',
  'kubernetes_namespace_creation',
  'kubernetes_image_creation',
  'kubernetes_node_creation',
  'kubernetes_pod_creation',
  'kubernetes_container_creation',
  'ecs_container_creation',
  'ecs_service_creation',
  'ecs_task_creation',
  'ecs_image_creation',
  'account_creation',
  'user_creation',
  'policy_inventory',
  'kubernetes_namespace_inventory',
  'kubernetes_node_inventory',
  'kubernetes_pod_inventory',
  'kubernetes_container_inventory',
  'kubernetes_image_inventory',
  'ecs_container_inventory',
  'ecs_image_inventory',
  'ecs_service_inventory',
  'ecs_task_inventory',
  'account_inventory',
  'user_inventory',
  'report_execution_inventory',
];

export interface StrictRawSystemStatistics extends Omit<
  RawSystemStatistics,
  'name'
> {
  name: SystemStatisticsType;
  metric_basis?: SystemStatisticsMetricBasis;
}

export type SystemStatistics =
  CamelCasedPropertiesDeep<StrictRawSystemStatistics>;

export type DetailedSystemStatistics = Omit<SystemStatistics, 'detail'> & {
  detail: NonNullable<SystemStatistics['detail']>;
};

export interface SystemStatisticsList {
  items?: DetailedSystemStatistics[];
}

export const SYSTEM_USAGE_METRIC_TYPES = [
  'sboms',
  'statelessSBOMs',
  'accounts',
  'users',
] as const;

export type SystemUsageMetricType = (typeof SYSTEM_USAGE_METRIC_TYPES)[number];

export interface SystemUsageMetricBase {
  /**
   * What the metric represents and how it is calculated.
   */
  description: string;
  /**
   * A human-readable label for the metric.
   */
  label: string;
}

export interface SystemUsageMetricConfig extends SystemUsageMetricBase {
  /**
   * One or more statistics types used to calculate the metric.
   */
  statisticsTypes: SystemStatisticsType[];
  /**
   * Whether to include historical data in the metric, which is typically used
   * to display a chart.
   */
  includeHistory?: boolean;
}

export type SystemUsageMetricHistory = Pick<
  DetailedSystemStatistics['detail'],
  'monthlyTwelveMonths' | 'dailyThirtyDays'
>;

export type SystemStatisticsOverTime =
  SystemUsageMetricHistory[keyof SystemUsageMetricHistory];

/**
 * Determines the collection basis of a given statistic, which can then be used
 * to inform the way it is presented and described in the UI.
 *
 * For example, the current number of accounts in the system is an `inventory`,
 * whereas the total number of accounts created over time is an `accumulator`.
 */
export type SystemUsageMetricBasis =
  NonNullable<SystemStatisticsMetricBasis> | 'unknown';

/**
 * Describes a single metric surfaced in the System Usage UI. This is a specific
 * UI representation of the underlying system statistics, and as such may be
 * constructed from multiple statistics.
 */
export interface SystemUsageMetric extends SystemUsageMetricBase {
  /**
   * Historical data points for the metric, gathered from all underlying
   * statistics.
   */
  history: SystemUsageMetricHistory | null;
  /**
   * The underlying statistics used to construct the metric.
   */
  statistics: DetailedSystemStatistics[];
  /**
   * The UI-specific metric type.
   */
  type: SystemUsageMetricType;
  /**
   * The calculated value of the metric.
   */
  value: number;
  /**
   * The collection basis of the metric.
   */
  basis: SystemUsageMetricBasis;
}

export interface SystemUsageData {
  /**
   * The list of metrics to display in the UI.
   */
  metrics: SystemUsageMetric[];
  /**
   * The raw system statistics data for download.
   */
  rawStatistics: RawSystemStatisticsList;
}

export type GetUsageResponse = EntityServiceResponse<SystemUsageData>;

// --- Health ------------------------------------------------------------------

export type SystemComponent = TupleToUnion<typeof SYSTEM_COMPONENTS>;

export type SystemStatusItem = TupleToUnion<typeof SYSTEM_STATUS_ITEMS>;

export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy';

export type HealthStatusBySystemComponent = Record<
  SystemComponent,
  HealthStatus | undefined
>;

export type Service = OverrideProperties<
  CamelCasedPropertiesDeep<RawService>,
  {
    statusMessage?: ServiceStatusMessage;
  }
>;

export type GetServicesResponse = EntityServiceResponse<Service[]>;

export type Feed = CamelCasedPropertiesDeep<RawFeedMetadata>;
export type FeedGroup = CamelCasedPropertiesDeep<RawFeedGroupMetadata>;

// Flattened data structure holding information about a FeedGroup along with the Feed metadata
export type FlattenedFeedGroup = Pick<Feed, 'datasetName' | 'datasetChecksum'> &
  FeedGroup & {
    feedName?: string;
  };

export type GetFeedsResponse = EntityServiceResponse<Feed[]>;

export interface ServiceHealthCounts {
  /**
   * Number of instances that are up and available
   */
  upCount: number;
  /**
   * Number of instances that are down and unavailable (i.e. there is a fault)
   */
  downCount: number;
  /**
   * Number of instances that are orphaned (detached, usually awaiting cleanup)
   */
  orphanedCount: number;
  /**
   * Total number of service instances (all states)
   */
  instancesCount: number;
}

export type ServiceHealthDetail = ServiceHealthCounts;

export type HealthDetailByServiceName = Record<
  ServiceName,
  ServiceHealthDetail
>;

export interface ServicesHealth extends ServiceHealthCounts {
  /**
   * Number of required services with at least one instance up
   */
  requiredUpCount: number;
  /**
   * All required services are available
   */
  hasAllRequiredServices: boolean;
  /**
   * Some optional services are down or having issues
   */
  hasSomeServiceIssues: boolean;
  /**
   * Detailed health information for each service
   */
  detailByName: HealthDetailByServiceName;
}

/**
 * Counts detailing the health of feed groups within a single feed or across
 * multiple feeds. The counts are calculated with cascading logic, meaning that
 * each count is derived from the previous count, through the following order:
 * total -> enabled -> synced -> empty.
 */
export interface FeedHealthCounts {
  /**
   * Total number of feed groups within enabled feeds
   */
  totalGroupsCount: number;
  /**
   * Number of enabled feed groups within enabled feeds
   */
  enabledGroupsCount: number;
  /**
   * Number of enabled feed groups within enabled feeds that have previously
   * synced
   */
  syncedGroupsCount: number;
  /**
   * Number of enabled feed groups within enabled feeds that have previously
   * synced but have no records. A zero count indicates that all groups have
   * records (the feed is fully synced).
   */
  emptyGroupsCount: number;
}

export type FeedHealthDetail = FeedHealthCounts;

export interface FeedsHealthCounts extends FeedHealthCounts {
  /**
   * Number of feeds that are enabled
   */
  enabledCount: number;
  /**
   * Total number of feeds
   */
  totalCount: number;
}

export interface FeedsHealth extends FeedsHealthCounts {
  /**
   * Feeds and groups exist and are enabled
   */
  hasFeeds: boolean;
  /**
   * All enabled feeds and groups have synced and have records
   */
  isFullySynced: boolean;
  /**
   * Detailed health information for each feed
   */
  detailByName: Record<string, FeedHealthDetail> | null;
}

export type Integration = CamelCasedPropertiesDeep<RawIntegrationSummary>;
export type GetIntegrationsResponse = EntityServiceResponse<
  Record<string, Integration>
>;
export type FetchIntegrationsActionParams = EntityActionParams & {
  onlyDegraded: boolean;
};

export interface CreateRegistryActionParams {
  name: string;
  registry: string;
  type: string;
  username: string;
  password: string;
  selfSigned: boolean;
  validate: boolean;
  masked?: boolean;
}

export type SystemRouteParams = RouteParams<{
  tab?: string;
  account?: string;
}>;
